import { Box, SimpleGrid, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback, useMemo } from 'react';

import { GET_ATTRIBUTE_LIST_BY_ENTITY } from 'packages/attribute/attribute.gql';
import {
  useCreateOrUpdateLatestAttributeValueMutation,
  useGetAttributeListByEntityQuery,
} from 'packages/attribute/attribute.gql.generated';
import { AttributeInput } from 'packages/common-frontend/components/AttributeInput/AttributeInput';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';
import {
  mapAttributeToGQLValue,
  mapGqlAttributeToAttribute,
} from 'packages/common-frontend/components/AttributeInput/utils';
import { Typography } from 'packages/common-frontend/components/Typography';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type PortfolioSummaryAttributesProps = {
  creditFacilityId: string;
};

export const PortfolioSummaryAttributes = ({
  creditFacilityId,
}: PortfolioSummaryAttributesProps) => {
  const user = useUserAuthOrThrow();
  const toast = useToast();
  const { data, error } = useGetAttributeListByEntityQuery({
    variables: {
      organizationId: user.user.organizationId,
      listName: 'creditFacilityCustomAttributes',
      entityDomain: 'creditFacility',
      entityIdentifier: creditFacilityId,
    },
  });

  if (error) throw error;

  const [
    createOrUpdateLatestAttributeValue,
  ] = useCreateOrUpdateLatestAttributeValueMutation({
    refetchQueries: [
      {
        query: GET_ATTRIBUTE_LIST_BY_ENTITY,
        variables: {
          organizationId: user.user.organizationId,
          listName: 'creditFacilityCustomAttributes',
          entityDomain: 'creditFacility',
          entityIdentifier: creditFacilityId,
        },
      },
    ],
  });

  const attributes = useMemo(
    () =>
      data?.attributes.map((attr) => mapGqlAttributeToAttribute(attr)) ?? [],
    [data],
  );

  const onUpdate = useCallback(
    async (updatedAttribute: Attribute) => {
      try {
        await createOrUpdateLatestAttributeValue({
          variables: {
            input: {
              entityDomain: 'creditFacility',
              entityIdentifier: creditFacilityId,
              attributeId: updatedAttribute.attributeId,
              ...mapAttributeToGQLValue(updatedAttribute),
            },
          },
        });
      } catch (err) {
        Sentry.captureException(err);
        toast({
          ...errorToastConfig,
          title: `Failed to update ${updatedAttribute.displayName} attribute.`,
        });
      }
    },
    [createOrUpdateLatestAttributeValue, toast, creditFacilityId],
  );

  // Won't return a skeleton here since some facilities might not have attributes
  if (attributes.length === 0) return null;

  return (
    <Box>
      <Typography variant="section-title">Attributes</Typography>
      <SimpleGrid
        columns={{ sm: 2, md: 4, lg: 4, xl: 4 }}
        gap="1.5rem"
        flexGrow={1}
      >
        {attributes.map((attribute) => (
          <AttributeInput
            key={attribute.attributeId}
            attribute={attribute}
            onUpdate={onUpdate}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};
